'use client';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { AlertTriangle, Check, RotateCw, Save, X } from 'lucide-react';
import { Suspense, lazy, useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { getFileContent } from './api';
import type { WorkspaceFileEntry } from './types';
import { useWorkspace } from './workspace-context';

// Heavy CodeMirror bundle stays behind React.lazy — only loaded when a user edits.
const CodeMirrorEditor = lazy(() => import('./code-mirror-editor'));

interface FileEditorProps {
  workspaceId: string;
  file: WorkspaceFileEntry;
  /** Text the editor opens with (from the already-loaded preview). */
  initialText: string;
  /** Version the initial text was loaded from — the optimistic-concurrency base. */
  initialVersion: number;
  mime: string;
  /** Leave edit mode (back to preview). */
  onClose: () => void;
}

type ConflictState = { currentVersion: number } | null;

/**
 * CodeMirror-backed text editor for a workspace file. Saves through the SAME
 * canonical write path uploads + tools use (ctx.writeText → POST /files), never
 * a parallel mechanism. Carries the loaded version as the base so a concurrent
 * agent/other write is caught: a stale save surfaces a conflict banner offering
 * reload-or-overwrite (WORKSPACE_FS_PLAN.md C2, the P1 optimistic-concurrency
 * story). Dirty state is tracked against the last saved text.
 */
export function FileEditor({
  workspaceId,
  file,
  initialText,
  initialVersion,
  mime,
  onClose,
}: FileEditorProps) {
  const { writeText } = useWorkspace();
  const [draft, setDraft] = useState(initialText);
  const [savedText, setSavedText] = useState(initialText);
  const [baseVersion, setBaseVersion] = useState(initialVersion);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState<ConflictState>(null);
  const [reloading, setReloading] = useState(false);

  const dirty = draft !== savedText;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  // NOTE: state is seeded once per mount. The caller MUST key this component on
  // file.path so switching files remounts with fresh state — we deliberately do
  // NOT re-seed on prop change, otherwise a post-save version bump (file.version
  // moves 1→2) would clobber the just-saved draft with the now-stale initialText.

  const doSave = useCallback(
    async (force: boolean) => {
      if (saving) return;
      setSaving(true);
      const res = await writeText(file.path, draft, mime, force ? undefined : baseVersion);
      setSaving(false);
      if (res.ok) {
        setSavedText(draft);
        setBaseVersion(res.data.version);
        setConflict(null);
        toast.success(`Saved ${file.path.split('/').pop()} (v${res.data.version})`);
        return;
      }
      if (res.conflict) {
        setConflict({ currentVersion: res.currentVersion });
        toast.error('This file changed since you opened it.');
        return;
      }
      toast.error(`Save failed: ${res.error}`);
    },
    [saving, writeText, file.path, draft, mime, baseVersion],
  );

  const reloadLatest = useCallback(async () => {
    setReloading(true);
    const res = await getFileContent(workspaceId, file.path);
    setReloading(false);
    if (!res.ok) {
      toast.error(`Could not reload: ${res.error}`);
      return;
    }
    // inlineText for small text; otherwise fetch the presigned URL.
    let latest = res.data.inlineText;
    if (typeof latest !== 'string') {
      try {
        const r = await fetch(res.data.url);
        latest = await r.text();
      } catch {
        toast.error('Could not reload the latest contents.');
        return;
      }
    }
    setDraft(latest);
    setSavedText(latest);
    // We refetched the head; its version is file.version from the refreshed list,
    // but the freshest signal is the conflict's currentVersion.
    setBaseVersion(conflict?.currentVersion ?? file.version);
    setConflict(null);
    toast.success('Reloaded the latest version.');
  }, [workspaceId, file.path, file.version, conflict]);

  // Cmd/Ctrl+S saves.
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (dirtyRef.current) void doSave(false);
      }
    },
    [doSave],
  );

  return (
    <div
      className="flex h-full min-h-0 flex-col"
      data-testid="file-editor"
      data-dirty={dirty ? 'true' : 'false'}
      onKeyDown={onKeyDown}
    >
      {/* Editor toolbar */}
      <div className="flex min-h-11 shrink-0 items-center gap-2 border-b border-border bg-muted/20 px-3">
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          {dirty ? (
            <>
              <span className="size-1.5 rounded-full bg-amber-500" aria-hidden />
              <span data-testid="dirty-indicator">Unsaved changes</span>
            </>
          ) : (
            <>
              <Check className="size-3.5 text-emerald-500" />
              <span>Saved</span>
            </>
          )}
          <span className="text-muted-foreground/60">· editing from v{baseVersion}</span>
        </span>
        <div className="ml-auto flex items-center gap-1">
          <Button
            variant="default"
            size="sm"
            className="h-7 gap-1.5 px-2 text-xs"
            onClick={() => void doSave(false)}
            disabled={!dirty || saving}
          >
            {saving ? <Spinner className="size-3.5" /> : <Save className="size-3.5" />}
            Save
          </Button>
          <Button variant="ghost" size="sm" className="h-7 gap-1.5 px-2 text-xs" onClick={onClose}>
            <X className="size-3.5" /> Close
          </Button>
        </div>
      </div>

      {/* Conflict banner */}
      {conflict && (
        <div
          className="flex flex-wrap items-center gap-2 border-b border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs"
          data-testid="conflict-banner"
        >
          <AlertTriangle className="size-4 text-amber-500" />
          <span className="text-amber-600 dark:text-amber-400">
            This file changed to v{conflict.currentVersion} since you opened it (you edited from v
            {baseVersion}). Saving now will overwrite that change.
          </span>
          <div className="ml-auto flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1.5 px-2 text-xs"
              onClick={reloadLatest}
              disabled={reloading}
            >
              {reloading ? <Spinner className="size-3.5" /> : <RotateCw className="size-3.5" />}
              Reload latest
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1.5 border-amber-500/40 px-2 text-xs text-amber-600 hover:text-amber-600 dark:text-amber-400"
              onClick={() => void doSave(true)}
              disabled={saving}
            >
              Overwrite anyway
            </Button>
          </div>
        </div>
      )}

      {/* Editor surface */}
      <div className={cn('min-h-0 flex-1 overflow-auto')}>
        <Suspense
          fallback={
            <div className="flex h-full items-center justify-center gap-2 p-6 text-sm text-muted-foreground">
              <Spinner className="size-4" /> Loading editor…
            </div>
          }
        >
          <CodeMirrorEditor path={file.path} value={draft} onChange={setDraft} />
        </Suspense>
      </div>
    </div>
  );
}
